import {
  Button,
  InputSelectControl,
  SettingsFormControl,
  SettingsGroup,
  Flex
} from '@sequencehq/core-components'
import { useNavigate } from 'react-router-dom'
import { Currency } from '@sequencehq/api'
import {
  accountDetailsTypeDescription,
  BankAccountDetails
} from 'SettingsV2/components/bank-details/bankDetails.types'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  isSequenceApiErrorResponse,
  isSequenceApiError,
  useMutation,
  useQuery
} from '@sequencehq/api'
import { FC, useState } from 'react'
import {
  getBankAccountsMap,
  validateAccountType,
  validateCurrency
} from 'SettingsV2/components/bank-details/bankDetails.utils'
import { SettingsPageLayout } from 'SettingsV2/components/SettingsPageLayout'
import { required } from '@sequencehq/utils/validation'
import { currencyToName, useForm } from '@sequencehq/utils'
import { BASE_INPUT_FIELD_STYLES } from 'SettingsV2/settings.constants'
import { GreyWhite } from '@sequencehq/core-components'
import { GreyGrey30 } from '@sequencehq/core-components'
import { useNotifications } from '@sequencehq/core-components'
import { usePaymentDetailsForm } from 'SettingsV2/components/bank-details/usePaymentDetailsForm'

export const BankDetailsNewSettingsPage: FC = () => {
  const navigate = useNavigate()

  const merchant = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  const accountSettings = useQuery(
    dashboard20240730Client.getSequenceAccountSettings
  )

  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { fields, queries } = useForm<{
    currency?: Currency
    accountType?: BankAccountDetails['type']
  }>({
    value: {
      currency: undefined,
      accountType: undefined
    },
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'currency',
        validation: [required]
      },
      {
        property: 'accountType',
        validation: [required]
      }
    ]
  })

  const { displayNotification } = useNotifications()

  const currency = validateCurrency(fields.currency.value)

  const accountType = validateAccountType(fields.accountType.value)

  const paymentDetailsForm = usePaymentDetailsForm({
    accountType: accountType?.key,
    showValidationErrors
  })

  const mutator = useMutation(dashboard20240730Client.putMerchant, {
    onSuccess() {
      displayNotification('Bank details created', {
        type: 'success'
      })
      navigate('/settings/bank-details')
    },
    onError(error) {
      const description =
        isSequenceApiError(error) && isSequenceApiErrorResponse(error.response)
          ? error.response.error.errors.at(0)?.message
          : undefined

      displayNotification('Could not create bank details', {
        type: 'error',
        description
      })
    }
  })

  const bankDetailsMap = getBankAccountsMap(merchant.data?.bankAccountDetails)

  const accountTypeOptions = Object.entries(accountDetailsTypeDescription)
    .filter(([key]) => !bankDetailsMap.get(`${currency?.key}_${key}`))
    .map(([value, label]) => ({ value, label }))

  const enabledCurrencies = accountSettings.data?.enabledCurrencies ?? []

  const currencyOptions = Object.entries(currencyToName)
    .filter(([value]) => enabledCurrencies.includes(value as Currency))
    .map(([value, label]) => ({
      value: value as Currency,
      label
    }))

  const backUrl = '/settings/bank-details'

  const canSubmit =
    queries.isValid &&
    queries.isDirty &&
    paymentDetailsForm?.queries.isValid &&
    paymentDetailsForm?.queries.isDirty &&
    accountSettings.data &&
    merchant.data &&
    !mutator.isPending

  return (
    <>
      <SettingsPageLayout
        header="New bank details"
        isError={accountSettings.isError}
        backButton={{
          href: backUrl,
          label: 'Bank details'
        }}
      >
        <SettingsGroup isLoading={accountSettings.isLoading} rows={2}>
          <SettingsFormControl label="Currency" htmlFor="currency">
            <InputSelectControl
              id="currency"
              data-testid="settings.bankDetails.currency"
              placeholder="Currency"
              triggerStyle={{
                width: BASE_INPUT_FIELD_STYLES.width
              }}
              options={currencyOptions}
              initialValue={fields.currency.value}
              onChange={value => {
                setShowValidationErrors(false)
                fields.currency.onChange(value as Currency)
              }}
              disabled={mutator.isPending}
            />
          </SettingsFormControl>

          <SettingsFormControl label="Account type" htmlFor="account-type">
            <InputSelectControl
              id="account-type"
              data-testid="settings.bankDetails.accountType"
              placeholder="Account type"
              triggerStyle={{
                width: BASE_INPUT_FIELD_STYLES.width
              }}
              disabled={mutator.isPending}
              options={accountTypeOptions}
              initialValue={fields.accountType.value}
              onChange={value => {
                setShowValidationErrors(false)
                fields.accountType.onChange(value as BankAccountDetails['type'])
              }}
            />
          </SettingsFormControl>
        </SettingsGroup>

        {paymentDetailsForm?.component}
      </SettingsPageLayout>

      <Flex
        justifyContent="end"
        gap="8px"
        px="12px"
        py="8px"
        backgroundColor={GreyWhite}
        borderTop={`1px solid ${GreyGrey30}`}
        position="sticky"
        bottom={0}
      >
        <Button variant="secondary" onClick={() => navigate(backUrl)}>
          Cancel
        </Button>

        <Button
          variant="primary"
          data-testid="settings.bankDetails.new.submit"
          disabled={!canSubmit}
          submitting={mutator.isPending}
          submittingText="Creating bank details..."
          onClick={() => {
            setShowValidationErrors(true)
            if (!canSubmit || !merchant.data) return
            // Build a new array rather than pushing into
            // merchant.data.bankAccountDetails: that is the React Query cache
            // reference, and mutating it in place makes the just-added account
            // type appear "already configured" (so it gets filtered out of the
            // options) even when the API rejects the details as invalid.
            const bankAccountDetails = paymentDetailsForm.value
              ? [
                  ...merchant.data.bankAccountDetails,
                  {
                    key: fields.currency.value!,
                    value: paymentDetailsForm.value
                  }
                ]
              : merchant.data.bankAccountDetails

            mutator.mutate({
              id: merchant.data.id,
              body: {
                legalCompanyName: merchant.data.legalCompanyName,
                address: merchant.data.address,
                email: merchant.data.email,
                bankAccountDetails
              }
            })
          }}
        >
          Create bank details
        </Button>
      </Flex>
    </>
  )
}
